import type { ReactNode } from "react";

import { useGo } from "../lib/useGo";
import { figureText } from "./Hero";
import { Tag } from "./Tag";

/**
 * Two or three figures of the day in one group, cells split by 1px rules (SNIPPETS §8).
 *
 * When to use: under Today's hero, or any page with 2–3 same-weight figures. Children are `Stat`s;
 * the columns follow their number.
 */
export function StatStrip({ children, ariaLabel }: { children: ReactNode; ariaLabel?: string }) {
  return (
    <div className="k-stats" role="group" aria-label={ariaLabel}>
      {children}
    </div>
  );
}

/**
 * One cell of a StatStrip. `figure: null` renders "–" and the tag stays (invariant 11).
 *
 * `estimated` puts "~" in front of the figure and an "est." Tag under it, unless a `tag` is given.
 * `to` makes the cell a link (`useGo`: across tabs it switches tab); `onPress` a button.
 */
export function Stat({
  figure,
  label,
  tag,
  note,
  estimated,
  to,
  onPress,
}: {
  figure: number | string | null;
  /** 12.8 muted: "Steps", "Work · kcal" */
  label: ReactNode;
  /** a Tag under the label */
  tag?: ReactNode;
  /** instead of a tag, a 12.8 note: "of ~1,810" */
  note?: ReactNode;
  estimated?: boolean;
  to?: string;
  onPress?: () => void;
}) {
  const navigate = useGo();
  const go = onPress ?? (to ? () => navigate(to) : undefined);
  const shown = figure === null ? "–" : `${estimated ? "~" : ""}${figureText(figure)}`;
  const foot = tag ?? (estimated ? <Tag tone="est" /> : null);

  const body = (
    <>
      <span className={`k-stat__num${figure === null ? " is-empty" : ""}`}>{shown}</span>
      <span className="k-stat__label">{label}</span>
      {foot ? (
        <span className="k-stat__foot">{foot}</span>
      ) : note ? (
        <span className="k-stat__foot">
          <span className="k-stat__note">{note}</span>
        </span>
      ) : null}
    </>
  );

  return go ? (
    <button type="button" className="k-stat" onClick={go}>
      {body}
    </button>
  ) : (
    <div className="k-stat">{body}</div>
  );
}
